// Centralized payment eligibility validation for shared-trip join/create flows.

import { supabase } from '../config/supabase';
import {
  evaluateTripJoinability,
  joinRejectionMessage,
} from '../config/sharedTransferRules';

export async function validatePaymentEligibility(tripId, _userEmail, participantId = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        canPay: false,
        reason: 'not_authenticated',
        message: 'You must be signed in to pay for this trip',
      };
    }

    const { data: existingParticipant, error: participantError } = await supabase
      .from('participants')
      .select('*')
      .eq('trip_request_id', tripId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (participantError) {
      throw new Error('Error checking existing participation: ' + participantError.message);
    }

    if (existingParticipant) {
      if (existingParticipant.payment_status === 'paid') {
        return {
          canPay: false,
          reason: 'already_paid',
          message: 'You have already paid for this trip',
        };
      }

      if (existingParticipant.payment_status === 'manual_resolution') {
        return {
          canPay: false,
          reason: 'manual_resolution',
          message: 'Your payment is being reviewed by our team. Please contact support — do not pay again.',
        };
      }

      if (existingParticipant.payment_status === 'cancelled') {
        return {
          canPay: false,
          reason: 'cancelled',
          message: 'Your booking was dismissed and a refund is pending. Please contact support — do not pay again.',
        };
      }

      if (existingParticipant.payment_status === 'authorized') {
        const stripeValidation = await validateStripePaymentStatus(tripId, existingParticipant.id, user.email);

        if (!stripeValidation.isValid) {
          await supabase
            .from('participants')
            .update({ payment_status: 'pending' })
            .eq('id', existingParticipant.id);
        } else {
          return {
            canPay: false,
            reason: 'already_authorized',
            message: 'Your payment is already authorized for this trip',
          };
        }
      }
    }

    const { data: trip, error: tripError } = await supabase
      .from('trip_requests_public')
      .select('*')
      .eq('id', tripId)
      .single();

    if (tripError) {
      throw new Error('Error loading trip: ' + tripError.message);
    }

    const { data: publicParticipants, error: participantsError } = await supabase
      .from('trip_participants_public')
      .select('payment_status, group_size')
      .eq('trip_request_id', tripId);

    if (participantsError) {
      throw new Error('Error loading trip participants: ' + participantsError.message);
    }

    if (trip.status !== 'open' && trip.status !== 'confirmed') {
      return {
        canPay: false,
        reason: 'trip_not_available',
        message: `Trip is ${trip.status} and no longer accepting payments`,
      };
    }

    const isExisting = !!existingParticipant;
    if (!isExisting) {
      const joinable = evaluateTripJoinability(trip);
      if (!joinable.ok) {
        return {
          canPay: false,
          reason: joinable.reason,
          message: joinRejectionMessage(joinable.reason),
        };
      }
    }

    const currentParticipants = (publicParticipants || []).filter(p =>
      p.payment_status === 'authorized' || p.payment_status === 'paid'
    );

    const totalSeats = currentParticipants.reduce((sum, p) => sum + (p.group_size || 1), 0);
    const maxSeats = trip.max_participants ?? trip.max_seats ?? 6;

    if (totalSeats >= maxSeats) {
      return {
        canPay: false,
        reason: 'trip_full',
        message: 'Trip is at maximum capacity',
      };
    }

    return {
      canPay: true,
      existingParticipant,
      trip,
      availableSeats: maxSeats - totalSeats,
    };
  } catch (error) {
    console.error('Payment eligibility validation failed:', error);
    return {
      canPay: false,
      reason: 'validation_error',
      message: 'Unable to validate payment eligibility: ' + error.message,
    };
  }
}

export async function validateStripePaymentStatus(tripId, participantId, userEmail) {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-payment-handler`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({
        action: 'validate_payment_intent_status',
        tripId,
        participantId,
        userEmail,
      }),
    });

    if (!response.ok) {
      console.warn('Stripe validation failed, assuming invalid');
      return { isValid: false, reason: 'stripe_unreachable' };
    }

    const result = await response.json();

    if (!result.success) {
      console.warn('Stripe validation returned error:', result.error);
      return { isValid: false, reason: result.error };
    }

    const validStatuses = ['requires_capture'];
    const isValid = validStatuses.includes(result.actualStatus);

    return {
      isValid,
      actualStatus: result.actualStatus,
      paymentIntentId: result.paymentIntentId,
    };
  } catch (error) {
    console.error('Stripe validation error:', error);
    return { isValid: false, reason: error.message };
  }
}
